/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState, useMemo } from "react";
import Card from "components/card";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import { LLM_CONSTANTS } from "constants/configCategory";
import { MdCheckCircle, MdCancel, MdOutlineError } from "react-icons/md";
import { format } from "date-fns";
import { useGlobalContext } from "context/globalContext";
import CreatePromptFragments from "../create-prompt-fragments/createPromptFragments";
import EditOrDeletePromptFragmentsBtn from "../edit-or-delete-prompt-fragments/editOrDeletePromptFragments";
import { getFilterPromptFragmentsAPI,getPromptFragmentsAPI } from "Api/promptFragments";
import Loader from "components/loader/Loader";
import FilterDropdown from "views/admin/tables/new-system-prompts/components/filter-dropdown/FilterDropdown";
import { consoleLog } from "helpers";

const PromptFragmentsTable = () => {
  const {
    fetchPromptFragments,
    setFetchPromptFragments,
    filterData,
    setPromptFragmentsFormData,
    setIsOpenPromptFragmentsPreviewModal,
    initialPromptFragmentsFormState,
  } = useGlobalContext();

  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filterTableData, setFilterTableData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilters, setSelectedFilters] = useState({});
  const [totalPages, setTotalPages] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);

  const handleDebounceSearchQuery = () => {
    let timeoutId;
    return function (value) {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setSearchQuery(value);
        setPageIndex(0);
      }, 300);
    };
  };

  const handleSearchQuery = handleDebounceSearchQuery();

  const selectedFilterArray = Object?.keys(selectedFilters)?.filter(
    (key) => selectedFilters[key] !== ""
  );

  const getFilterPromptsFragments = useCallback(async () => {
    if (!searchQuery && selectedFilterArray?.length === 0) return;
    let payload;
    if (searchQuery && selectedFilterArray?.length > 0) {
      const filterObj = Object?.keys(selectedFilters)
        ?.filter((key) => selectedFilters[key] !== "")
        ?.reduce((acc, key) => ({ ...acc, [key]: selectedFilters[key] }), {});
      payload = filterObj?.status
        ? {
            searchQuery,
            ...{ ...filterObj, status: filterObj?.status === "Active" },
          }
        : {
            searchQuery,
            ...filterObj,
          };
    }
    if (searchQuery && selectedFilterArray?.length === 0)
      payload = {
        searchQuery,
      };
    if (!searchQuery && selectedFilterArray?.length > 0) {
      const filterObj = Object?.keys(selectedFilters)
        ?.filter((key) => selectedFilters[key] !== "")
        ?.reduce((acc, key) => ({ ...acc, [key]: selectedFilters[key] }), {});
      payload = filterObj?.status
        ? { ...{ ...filterObj, status: filterObj?.status === "Active" } }
        : {
            ...filterObj,
          };
    }
    const token = localStorage.getItem("jwtToken");

    const skip = pageIndex * 10;
    const limit = 10;

    const response = await getFilterPromptFragmentsAPI(
      { searchQuery, pageIndex, limit, skip },
      payload,
      setIsLoading,
      token
    );
    if (response) {
      setFilterTableData(response.data);
      setTotalPages(response.totalPages);
      setFetchPromptFragments(false);
    }
    if (!response) {
      setFilterTableData([]);
    }
  }, [
    searchQuery,
    pageIndex,
    selectedFilterArray?.length,
    selectedFilters,
    setFetchPromptFragments,
  ]);

  const getPromptFragments = useCallback(async () => {
    if (searchQuery !== "" || selectedFilterArray?.length > 0) return;

    const token = localStorage.getItem("jwtToken");
    const skip = 10;

    const response = await getPromptFragmentsAPI(
      {
        skip,
        pageIndex,
      },
      setIsLoading,
      token
    );

    if (response) {
      if (response.data.length === 0 && pageIndex > 0) {
        setPageIndex((prev) => prev - 1);
      } else {
        setTableData(response.data);
        setTotalPages(response?.pagination?.totalPages || 0);
        setFetchPromptFragments(false);
      }
    }
  }, [
    searchQuery,
    pageIndex,
    selectedFilterArray?.length,
    setFetchPromptFragments,
  ]);

 useEffect(() => {
  const fetchData = async () => {
    setIsLoading(true);

    if (searchQuery || selectedFilterArray.length > 0) {
      await getFilterPromptsFragments();
    } else {
      await getPromptFragments();
    }

    setIsLoading(false);
  };

  fetchData();
}, [searchQuery, selectedFilters, pageIndex, fetchPromptFragments]);


  const columnsData = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
      },
      {
        Header: "Key",
        accessor: "key",
      },
      {
        Header: "Version",
        accessor: "version",
      },
      {
        Header: "Value",
        accessor: "value",
      },
      {
        Header: "Status",
        accessor: "isActive",
        sortType: (rowA, rowB) => {
          const a = rowA.values.isActive;
          const b = rowB.values.isActive;
          if (a === b) return 0;
          return a ? -1 : 1;
        },
      },
      {
        Header: "LLM",
        accessor: "LLM",
      },
      {
        Header: "Comment",
        accessor: "comment",
      },
      {
        Header: "Actions",
        accessor: "actions",
      },
    ],
    []
  );

  const columns = useMemo(() => columnsData, [columnsData]);

  const tableInstance = useTable(
    {
      columns,
      data:
        searchQuery || selectedFilterArray.length > 0
          ? filterTableData
          : tableData,

      manualPagination: true,
      pageCount: totalPages,
      pageIndex,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    gotoPage,
  } = tableInstance;

  const filters = filterData?.find(
    (filter) => filter?.table === "promptFragment"
  )?.filters;

  const handleOpenPreviewModal = useCallback(
    (cell) => {
        consoleLog(['Preview clicked for:', cell?.row?.original]);
      setPromptFragmentsFormData(
        cell?.row?.original
          ? {
              ...cell.row.original,
              isActive: cell.row.original.isActive ? "active" : "inactive",
            }
          : {
              ...initialPromptFragmentsFormState,
              isActive: initialPromptFragmentsFormState.isActive
                ? "active"
                : "inactive",
            }
      );
      setIsOpenPromptFragmentsPreviewModal(true);
    },
    [
      initialPromptFragmentsFormState,
      setIsOpenPromptFragmentsPreviewModal,
      setPromptFragmentsFormData,
    ]
  );

  const handlePageChange = (direction) => {
    if (direction === "next" && pageIndex < totalPages - 1) {
      setPageIndex((prevPageIndex) => {
        const newPageIndex = prevPageIndex + 1;
        gotoPage(newPageIndex);
        return newPageIndex;
      });
    }

    if (direction === "previous" && pageIndex > 0) {
      setPageIndex((prevPageIndex) => {
        const newPageIndex = prevPageIndex - 1;
        gotoPage(newPageIndex);
        return newPageIndex;
      });
    }
  };
  const getLLMType = (value) => {
    const found = LLM_CONSTANTS.find((item) => item.value === value);
    return found ? found.type : value; // fallback to original value if not found
  };

  return (
    <Card extra={"w-full h-full p-4 sm:overflow-x-auto "}>
      <div className="relative flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div className="flex w-full flex-col gap-3 sm:max-w-lg sm:flex-row">
          <div className="relative w-full sm:w-64">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2 text-lg leading-none text-gray-400">
              🔍
            </span>
            <input
              type="search"
              className="w-full rounded-md border border-gray-300 py-2 pl-8 pr-2 text-sm transition-all duration-300 ease-in-out hover:border-blue-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white"
              placeholder="Search"
              onChange={(e) => {
                e.preventDefault();
                handleSearchQuery(e.target.value);
              }}
            />
          </div>

          {filters?.length > 0 && (
            <FilterDropdown
              filters={filters}
              onFilterSelect={(selectedFilters) => {
                consoleLog(["Selected filters:", selectedFilters]);
                setSelectedFilters(selectedFilters);
                setPageIndex(0);
              }}
              finalFilters={selectedFilters}
            />
          )}
        </div>

        <CreatePromptFragments />
      </div>

      <div className="mt-4 h-full overflow-x-scroll xl:overflow-auto">
        {isLoading && tableData.length === 0 && (
          <div className="flex h-[400px] w-full items-center justify-center">
            <Loader />
          </div>
        )}
        {!isLoading &&
          (searchQuery || selectedFilterArray?.length > 0) &&
          filterTableData.length === 0 && (
            <span className="block w-full text-center"> No data found.</span>
          )}
        {!isLoading && tableData.length === 0 && (
          <span className="block w-full text-center"> No data found.</span>
        )}
        {((!!tableData.length &&
          !searchQuery &&
          !selectedFilterArray?.length) ||
          (!!filterTableData.length &&
            (searchQuery || selectedFilterArray?.length > 0))) && (
          <table {...getTableProps()} className="w-full">
            <thead>
              {headerGroups.map((headerGroup, index) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={index}>
                  {headerGroup.headers.map((column, index) => (
                    <th
                      key={index}
                      className="border-b border-gray-100 bg-gray-50 py-3 px-4 text-left text-sm text-gray-600 dark:!bg-black-900 "
                    >
                      {column.render("Header")}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row, index) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={index}>
                    {row.cells.map((cell, index) => (
                      <td
                        {...cell.getCellProps()}
                        key={index}
                        onClick={() => {
  const previewableColumns = ["name", "key", "version", "value", "isActive", "LLM", "comment"];
  if (previewableColumns.includes(cell.column.id)) {
    handleOpenPreviewModal(cell);
  }
}}
                        className="border-b border-gray-100 py-4 px-4 align-top text-sm cursor-pointer"
                      >
                        {cell.column.id === "actions" ? (
                          <EditOrDeletePromptFragmentsBtn
                            cell={cell}
                            handleOpenPreviewModal={handleOpenPreviewModal}
                          />
                        ) : cell.column.id === "isActive" ? (
                          <div className="flex items-center justify-center">
                            {cell?.value === true ? (
                              <MdCheckCircle className="text-green-500" />
                            ) : cell?.value === false ? (
                              <MdCancel className="text-red-500" />
                            ) : (
                              <MdOutlineError className="text-yellow-500" />
                            )}
                          </div>
                        ) : cell.column.id === "createdAt" ||
                          cell.column.id === "updatedAt" ? (
                          <span>
                            {cell?.value
                              ? format(new Date(cell?.value), "dd/MM/yyyy")
                              : "N/A"}
                          </span>
                        ) : cell.column.id === "value" ? (
                          <div className="max-h-24 overflow-y-auto whitespace-pre-wrap">
                            {cell?.value}
                          </div>
                        ) : cell.column.id === "LLM" ? (
                          getLLMType(cell?.value)
                        ) : (
                          cell?.value
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {((!!tableData.length && !searchQuery) ||
        (!!filterTableData.length && searchQuery)) && (
        <div className="mt-4 flex items-center justify-between">
          <button
            onClick={() => handlePageChange("previous")}
            disabled={pageIndex === 0}
            className={`rounded-md px-4 py-2 text-sm font-medium text-white ${
              pageIndex > 0
                ? "bg-blue-500 hover:bg-blue-600"
                : "cursor-not-allowed bg-gray-300"
            }`}
          >
            Previous
          </button>

          <span className="text-sm text-gray-700 dark:text-white">
            Page <strong>{pageIndex + 1}</strong> of{" "}
            <strong>{totalPages}</strong>
          </span>

          <button
            onClick={() => handlePageChange("next")}
            disabled={pageIndex >= totalPages - 1}
            className={`rounded-md px-4 py-2 text-sm font-medium text-white ${
              pageIndex < totalPages - 1
                ? "bg-blue-500 hover:bg-blue-600"
                : "cursor-not-allowed bg-gray-300"
            }`}
          >
            Next
          </button>
        </div>
      )}
    </Card>
  );
};

export default PromptFragmentsTable;
